/**
 * Created by rathompson on 5/2/22
 * File: index
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Button, LoadingIcon, Tooltip, useModal } from '@panwds/react-ui'
import RCTree from '../common/components/page/rcTree/RCTree'
import BreadCrumbs from '../common/components/breadcrumbs/BreadCrumbs'
import { LoadDevices, LoadInstances, LoadAppFirewallLicenses, LoadNotEligibleDASApps, LoadSKUCompatibleDevices, LoadDeviceLicenses } from '../common/actions/GlobalActions'
import AssociationTable from './components/associationTable/AssociationTable'
import NoDataScreen from '../common/components/noDataScreen/NoDataScreen'
import { ARRAY, isNonemptyArray, indexArray } from '../common/utils/Utils'
import AddDeviceModal from './components/modal/AddDeviceModal'
import ZIndex from '../common/components/zIndex/ZIndex'
import {
  BUTTON_ADD_DEVICE,
  BUTTON_ADD_DEVICES,
  BUTTON_ASSOCIATE_APPS,
  BUTTON_LEARN_MORE,
  DAS_LEARN_MORE_INK,
  DEVICE_DESC_NO_DATA,
  DEVICE_HEADING_NO_DATA,
  TENANT_HAS_NO_APPS,
} from './constants/LabelConstants'
import { useMessage } from '../common/providers/MessageProvider'
import AppContext from '../common/context/AppContext'
import GlobalContext from '../common/context/GlobalContext'
import AssociateAppModal from './components/modal/associateAppModal/AssociateAppModal'
import { DEVICES_PERMISSION_SET } from '../common/constants/GlobalConstants'
import _ from 'lodash'
import useReadOnly from '../common/hooks/useReadOnly'
import TooltipButton from '../common/components/tooltipButton/TooltipButton'
import { TOOLTIP_READ_ONLY } from '../iam/constants/LabelConstants'
import IpRestrictProvider, { useIpRestrictionContext } from '../common/context/IpRestrictContext'
import IPRestrictionWrapper from '../common/components/noAccess/IPRestrictionWrapper'

const unsupportedDeviceTypes = ['vm', 'vmflex', 'cngfw','vm-panorama']

const DevicesContent = ({ onBreadCrumbClick = () => null }) => {

  const { apps: appsMap, getAppName } = useContext(AppContext)
  const { licenseReducer: { userSupportAccountsInfo }, treeReducer, dispatch } = useContext(GlobalContext)
  const { selectedTreeNode, breadcrumbs } = treeReducer
  const { createBanner, clearBanner, apiBannerHandler } = useMessage()

  const [loading, setLoading] = useState(false)
  const [devices, setDevices] = useState([])
  const [notEligibleApps, setNotEligibleApps] = useState([])
  const [disableAssociateBtn, setDisableAssociateBtn] = useState(false)
  const [instances, setInstances] = useState([])
  const ref = useRef(selectedTreeNode)

  const addDeviceModal = useModal(AddDeviceModal)
  const appModal = useModal(AssociateAppModal)

  const { setRestricted, checkIpRestricted } = useIpRestrictionContext()

  const readOnly = useReadOnly(selectedTreeNode?.id, DEVICES_PERMISSION_SET)

  useEffect(() => {
    return clearBanner
  }, [clearBanner])

  const createMessage = useCallback((appearance = 'error', message, time) => {
    createBanner({ message, bannerProps: { appearance }, ...(Boolean(time) && { time }) })
  }, [createBanner])

  const setError = useCallback((error) => {
    if (!checkIpRestricted(error)) {
      apiBannerHandler(error)
    }
  }, [apiBannerHandler, checkIpRestricted])

  const apps = useMemo(() => {
    const map = {}
    for (const [app_id, app] of Object.entries(appsMap)) {
      if (app.hasOwnProperty('device_association')) {
        map[app_id] = app
      }
    }
    return map
  }, [appsMap])


  const fetchNotEligibleDASApps = useCallback(async () => {
    setLoading(true)
    setRestricted(false)
    try {
      const tsg_id = selectedTreeNode?.id
      const resp = await LoadNotEligibleDASApps({ tsg_id })
      if (ref.current.id === tsg_id) {
        const nonDASApps = isNonemptyArray(resp) ? resp : []
        setNotEligibleApps(nonDASApps)
        setDisableAssociateBtn(false)
      }
    }
    catch (error) {
      setDisableAssociateBtn(true)
      setError(error)
    }
    setLoading(false)
  }, [selectedTreeNode?.id, setError, setRestricted])

  useEffect(() => {
    const appDefs = indexArray(Object.values(apps), 'app_id')
    const appSetForDASValidation = new Set(appDefs?.filter(appDef => appDef?.device_association?.validate_device)?.map(e => e.app_id))
    const isValidateDASApps = instances?.some(instance => appSetForDASValidation.has(instance.app_id))
    if (isValidateDASApps) {
      fetchNotEligibleDASApps()
    }
  }, [apps, fetchNotEligibleDASApps, instances])

  const fetchDevices = useCallback(async () => {
    const tsg_id = selectedTreeNode?.id
    setRestricted(false)
    try {
      const devices = await LoadDevices(tsg_id, dispatch)
      if (ref.current.id === tsg_id) {
        const _devices = devices.map(each => {
          if (each.associations) {
            each.associations = each.associations.map(assoc => {
              assoc.display_name = getAppName(each.app_id)
              return assoc
            })
            if (unsupportedDeviceTypes.includes(each.device_type)) {
              each.disabled = true
              each.reason = 'This is device is not eligible for any actions.'
            }
          }
          return each
        })
        const sortedDevices = _.orderBy(_devices, ['disabled'], ['asc'])// disabled should be at the bottom, so 'asc'
        setDevices(sortedDevices)
      }
    }
    catch (error) {
      if (ref.current.id === tsg_id) {
        setError(error)
      }
    }
    try {
      const instances = await LoadInstances({ tsg_id }, dispatch)
      if (ref.current.id === tsg_id) {
        if (instances.error) {
          checkIpRestricted(instances.error)
        }
        setInstances(instances)
      }
    }
    catch (error) {
      if (ref.current.id === tsg_id) {
        setError(error)
      }
    }
  }, [checkIpRestricted, dispatch, getAppName, selectedTreeNode?.id, setError, setRestricted])

  const fetchData = useCallback(async (tsg_id, dispatch) => {
    setLoading(true)
    setRestricted(false)
    try {
      const devices = await LoadDevices(tsg_id, dispatch)
      if (ref.current.id === tsg_id) {
        const _devices = devices?.map((d) => {
          if (unsupportedDeviceTypes.includes(d.device_type)) {
            d.disabled = true
            d.reason = 'This is device is not eligible for any actions.'
          }
          return d
        })
        const sortedDevices = _.orderBy(_devices, ['disabled'], ['asc']) // disabled should be at the bottom, so 'asc'
        setDevices(sortedDevices)
      }
    }
    catch (error) {
      if (ref.current.id === tsg_id) {
        setError(error)
      }
    }
    try {
      const instances = await LoadInstances({ tsg_id }, dispatch)
      if (ref.current.id === tsg_id) {
        if (instances.error) {
          checkIpRestricted(instances.error)
        }
        setInstances(instances)
      }
    }
    catch (error) {
      setError(error)
    }
    setLoading(false)
  }, [checkIpRestricted, setError, setRestricted])

  const fetchAppFirewallLicenses = useCallback(async (selectedApp) => {
    const { app_id, support_account_id } = selectedApp
    const isEla = userSupportAccountsInfo.find(e => e.accountid === +support_account_id)?.is_ela || false
    const support_account_ids = isEla ? [+support_account_id] : userSupportAccountsInfo.filter(e => !e.is_ela)?.map(e => e.accountid)
    const tsg_id = selectedTreeNode?.id
    return await LoadAppFirewallLicenses({ tsg_id, support_account_ids, app_id }, dispatch)
  }, [dispatch, selectedTreeNode?.id, userSupportAccountsInfo])

  const fetchSkuCompatibleDevices = useCallback(async (license_skus) => {
    const tsg_id = selectedTreeNode?.id
    return await LoadSKUCompatibleDevices({ tsg_id, license_skus }, dispatch)
  }, [dispatch, selectedTreeNode?.id, userSupportAccountsInfo])

  const fetchDeviceLicenses = useCallback(async (serial_numbers) => {
    const tsg_id = selectedTreeNode?.id
    return await LoadDeviceLicenses({ tsg_id, serial_numbers }, dispatch)
  }, [dispatch, selectedTreeNode?.id, userSupportAccountsInfo])


  useEffect(() => {
    const tsg_id = selectedTreeNode?.id
    if (tsg_id !== ref.current?.id) {
      setDevices([])
      setInstances([])
      setNotEligibleApps([])
      setDisableAssociateBtn(false)
    }
    ref.current = selectedTreeNode
    if (tsg_id) {
      fetchData(tsg_id, dispatch)
    }
  }, [fetchData, selectedTreeNode, dispatch])

  const hasInstances = Boolean(instances?.length)

  const products = useMemo(() => {
    const products = []
    const eligibleApps = Object.values(apps)?.filter(e => !notEligibleApps?.includes(e?.app_id))
    for (const app of eligibleApps) { // apps list is already filtered based on DAS enable
      const { device_association, app_id } = app
      const instance = instances.find(instance => instance.app_id === app_id)
      const isDasAllowedApp = device_association?.enabled && (!device_association?.restricted_accounts || device_association?.restricted_accounts.some(e => e === +instance?.support_account_id))
      if (instance && isDasAllowedApp) {
        products.push(instance)
      }
    }
    return products
  }, [apps, instances, notEligibleApps])
  const hasRequiredApps = useMemo(() => ARRAY.hasArray(products), [products])
  const addDeviceCallback = useCallback((data) => {
    // const newDevices = Array.isArray(data) ? data : data.items
    createMessage('success', `${data?.items?.insertedCount} devices have been added to ${selectedTreeNode?.display_name}`)
    // setNewDevices(newDevices)
    // tableRef.current?.toggleAllRowsSelected(false)
  }, [createMessage, selectedTreeNode?.display_name])

  const onAddDeviceClick = useCallback(() => {
    // setNewDevices([])
    addDeviceModal.openModal({ tenant: selectedTreeNode, dispatch, fetchDevices, callback: addDeviceCallback, instances })
  }, [addDeviceModal, selectedTreeNode, dispatch, fetchDevices, addDeviceCallback, instances])

  const onAssociateClick = useCallback(() => {
    appModal.openModal({
      tenant: selectedTreeNode,
      devices,
      instances,
      products,
      fetchAppFirewallLicenses,
      callback: ({ result }) => {
        const { deviceFailureCount, deviceSuccessCount, errorSessionId } = result
        if (errorSessionId) {
          createMessage('warning', `${deviceSuccessCount} device(s) associating with application, ${deviceFailureCount} devices failed to associated, please copy error session_id: ${errorSessionId}`, 30000) //30 secs
        }
        else {
          createMessage('success', `${deviceSuccessCount} device(s) associating with application`)
        }
      },
      fetchDevices,
      fetchSkuCompatibleDevices,
      fetchDeviceLicenses
    })
    // setNewDevices([])
  }, [appModal, selectedTreeNode, devices, instances, products, fetchAppFirewallLicenses, fetchDevices, createMessage, fetchSkuCompatibleDevices])


  const renderAddButtons = (devices) => {
    return <div className='tw-flex tw-flex-row tw-justify-between tw-p-3 tw-flex-row-reverse'>
      <div className='tw-flex tw-flex-row tw-gap-2'>
        {hasInstances && <Button
          appearance='secondary'
          onClick={() => onAddDeviceClick()}
          buttonPosition={'right'}
          disabled={readOnly}
        >
          {BUTTON_ADD_DEVICE}
        </Button>}
        {hasRequiredApps ? (
          <Button
            appearance='secondary'
            buttonPosition={'right'}
            disabled={disableAssociateBtn || readOnly}
            onClick={() => onAssociateClick()}
          >
            {BUTTON_ASSOCIATE_APPS}
          </Button>
        ) : (
          <Tooltip label={TENANT_HAS_NO_APPS}>
            <Button
              appearance='secondary'
              disabled
              onClick={() => onAssociateClick()}
              buttonPosition={'right'}
            >
              {BUTTON_ASSOCIATE_APPS}
            </Button>
          </Tooltip>
        )}
        <Button
          dataTestId="das-learn-more"
          appearance='clear'
          onClick={() => window.open(DAS_LEARN_MORE_INK)} >
          {BUTTON_LEARN_MORE}
        </Button>
      </div>
    </div>
  }
  return (
    <ZIndex>
      <div className="tw-flex tw-flex-1 tw-flex-row tw-absolute tw-inset-0">
        <div className='common-module-side-bar dark:cm-bg-gray-950'>
          <RCTree
            {...{ ...treeReducer, dispatch }}
            expandedKeys={[selectedTreeNode?.parent_id]}
          />
        </div>
        <IPRestrictionWrapper>
          <div className="tw-border-t tw-flex tw-flex-col tw-flex-1 tw-overflow-hidden tw-truncate">
            <div className="tw-p-3" style={{ minHeight: '63px' }}>
              <BreadCrumbs breadcrumbs={breadcrumbs} onClick={onBreadCrumbClick} />
            </div>
            {ARRAY.hasArray(devices) && renderAddButtons(devices)}
            <div className='tw-p-3 tw-flex-1 tw-relative'>
              {loading ? (
                <div
                  className="tw-flex tw-absolute tw-inset-0 tw-justify-center tw-items-center tw-z-50">
                  <LoadingIcon size="4xl" />
                </div>
              ) : (
                <>
                  {ARRAY.hasArray(devices) ? (
                    <AssociationTable {...{
                      selectedTreeNode,
                      fetchDevices,
                      dispatch,
                      devices,
                      addDeviceModal,
                      instances,
                      createMessage,
                      setError,
                      products,
                    }} />
                  ) : (
                    <NoDataScreen
                      actions={(
                        <>
                          <Button
                            dataTestId="das-learn-more"
                            appearance='clear'
                            onClick={() => window.open(DAS_LEARN_MORE_INK)} >
                            {BUTTON_LEARN_MORE}
                          </Button>
                          {readOnly ? (
                            <TooltipButton
                              label={TOOLTIP_READ_ONLY}
                              disabled
                              tooltipProps={{ defaultPlacement: 'bottom-end' }}
                              buttonProps={{ appearance: 'primary' }}>
                              {BUTTON_ADD_DEVICES}
                            </TooltipButton>
                          ) : hasInstances ? (
                            <Button
                              appearance='primary'
                              onClick={() => addDeviceModal.openModal({ tenant: selectedTreeNode, dispatch, fetchDevices, instances })}
                            >{BUTTON_ADD_DEVICES}</Button>
                          ) : (
                            <Button
                              appearance='primary'
                              disabled
                              onClick={() => null}
                            >
                              {BUTTON_ADD_DEVICES}
                            </Button>
                          )}
                        </>
                      )}
                      heading={hasInstances ? DEVICE_HEADING_NO_DATA : TENANT_HAS_NO_APPS}
                      description={hasInstances && DEVICE_DESC_NO_DATA}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </IPRestrictionWrapper>
      </div>
    </ZIndex>
  )
}

const Devices = (props) => (
  <IpRestrictProvider>
    <DevicesContent {...props} />
  </IpRestrictProvider>
)

export default Devices
